import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, TENANT_STATUS, adminApi, formatIstDate } from '@wa/shared';
import type { AdminTenant } from '@wa/shared';
import { Cell, DataTable, Empty, Page } from '@/components/Primitives';

/**
 * Every client, and the lifecycle controls that used to be a SQL statement.
 *
 * Suspension is not deletion and closing is not either: a tenant owns messages,
 * invoices and audit rows, and removing it would break that history or cascade
 * through and destroy it. Nothing on this platform deletes a client.
 */
export function TenantsPage() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [suspending, setSuspending] = useState<AdminTenant | null>(null);
  const [addingAgentTo, setAddingAgentTo] = useState<AdminTenant | null>(null);
  const [created, setCreated] = useState<string | null>(null);

  const tenants = useQuery({ queryKey: ['admin', 'tenants'], queryFn: adminApi.tenants });

  const done = () => {
    setError(null);
    void queryClient.invalidateQueries({ queryKey: ['admin', 'tenants'] });
  };

  const fail = (e: unknown, fallback: string) => setError(e instanceof ApiError ? e.message : fallback);

  const suspend = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => adminApi.suspendTenant(id, reason),
    onSuccess: () => { done(); setSuspending(null); },
    onError: (e) => fail(e, 'Could not suspend that client.'),
  });

  const reactivate = useMutation({
    mutationFn: (id: number) => adminApi.reactivateTenant(id),
    onSuccess: done,
    onError: (e) => fail(e, 'Could not reactivate that client.'),
  });

  const addAgent = useMutation({
    mutationFn: ({ tenantId, body }: {
      tenantId: number; body: { email: string; displayName: string; password: string };
    }) => adminApi.createTenantAgent(tenantId, body),
    onSuccess: (agent) => {
      done();
      setAddingAgentTo(null);
      setCreated(`${agent.displayName} (${agent.email}) can now sign in as an Administrator.`);
    },
    onError: (e) => fail(e, 'Could not create that agent.'),
  });

  return (
    <Page title="Tenants" subtitle="Every client on the platform, and what can be done to one.">
      {error && (
        <p role="alert" className="mb-3 rounded border border-(--color-bad) px-3 py-2 text-small text-(--color-bad)">
          {error}
        </p>
      )}

      {created && (
        <p className="mb-3 rounded border border-(--color-good) px-3 py-2 text-small text-(--color-good)">
          {created}
        </p>
      )}

      {/* The slug moved under the company name and the counts merged into one
          column. Eight columns plus a button group ran the actions off the right of
          a 1280 screen: the table scrolled, so every layout assertion passed, and
          the only controls on the page were invisible without scrolling for them. */}
      <DataTable head={['Company', 'Status', 'Balance', 'Numbers · people', 'Messages 30d', 'Created', '']}>
        {tenants.data?.map((t) => (
          <tr key={t.id}>
            <Cell>
              <span className="font-medium">{t.companyName}</span>
              <span className="block text-small text-(--color-muted)">{t.contactEmail}</span>
              <span className="block font-mono text-micro text-(--color-muted)">{t.slug}</span>
            </Cell>
            <Cell nowrap>
              <span className={t.status === 1 ? 'text-(--color-good)' : 'text-(--color-warn)'}>
                {TENANT_STATUS[t.status] ?? t.status}
              </span>
            </Cell>
            <Cell nowrap>{t.balance === null ? '—' : `₹${t.balance.toFixed(2)}`}</Cell>
            <Cell nowrap>{t.channels} · {t.agents}</Cell>
            <Cell nowrap>{t.messages30d.toLocaleString('en-IN')}</Cell>
            <Cell nowrap>{formatIstDate(t.createdAt)}</Cell>
            <Cell>
              <div className="flex flex-wrap gap-2">
                {t.status === 1 && (
                  <Action onClick={() => { setSuspending(t); setCreated(null); }}>Suspend</Action>
                )}
                {t.status === 0 && (
                  <Action onClick={() => reactivate.mutate(t.id)}>Reactivate</Action>
                )}
                {/* Closed is closed: a client that has been wound up does not get new
                    accounts, and reopening one is a billing decision, not a button. */}
                {t.status !== 2 && (
                  <Action onClick={() => { setAddingAgentTo(t); setCreated(null); }}>Add agent</Action>
                )}
              </div>
            </Cell>
          </tr>
        ))}
      </DataTable>

      {tenants.data?.length === 0 && <Empty>No tenants yet.</Empty>}

      {suspending && (
        <Dialog title={`Suspend ${suspending.companyName}`} onClose={() => setSuspending(null)}>
          <p className="mb-3 text-small text-(--color-muted)">
            Their agents are signed out immediately and cannot sign back in. Incoming messages are
            still received and stored, so nothing a customer sends is lost. Sends stop.
          </p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const reason = String(new FormData(e.currentTarget).get('reason') ?? '').trim();
              suspend.mutate({ id: suspending.id, reason });
            }}
          >
            <label className="text-small">
              <span className="mb-1 block text-(--color-muted)">
                Reason — the client is shown this on the sign-in screen
              </span>
              <input
                name="reason" required maxLength={200} autoFocus
                placeholder="Non-payment, invoice PSM/2026-27/000123"
                className="w-full rounded border border-(--color-line) bg-(--color-canvas) px-2 py-1.5 text-body"
              />
            </label>
            <div className="mt-4 flex gap-2">
              <button type="submit" disabled={suspend.isPending}
                      className="rounded bg-(--color-bad) px-3 py-1.5 text-body font-medium text-white disabled:opacity-50">
                {suspend.isPending ? 'Suspending…' : 'Suspend'}
              </button>
              <button type="button" onClick={() => setSuspending(null)}
                      className="rounded border border-(--color-line) px-3 py-1.5 text-body">
                Cancel
              </button>
            </div>
          </form>
        </Dialog>
      )}

      {addingAgentTo && (
        <Dialog title={`Add an administrator to ${addingAgentTo.companyName}`} onClose={() => setAddingAgentTo(null)}>
          <p className="mb-3 text-small text-(--color-muted)">
            They are created on this client&rsquo;s <b>Administrator</b> role. Pass the password on
            yourself, or tell them to use <b>Forgot password</b> on the client sign-in screen.
          </p>
          <form
            className="grid gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              const form = new FormData(e.currentTarget);
              addAgent.mutate({
                tenantId: addingAgentTo.id,
                body: {
                  displayName: String(form.get('displayName') ?? '').trim(),
                  email: String(form.get('email') ?? '').trim(),
                  password: String(form.get('password') ?? ''),
                },
              });
            }}
          >
            <label className="text-small">
              <span className="mb-1 block text-(--color-muted)">Name</span>
              <input name="displayName" required autoFocus
                     className="w-full rounded border border-(--color-line) bg-(--color-canvas) px-2 py-1.5 text-body" />
            </label>
            <label className="text-small">
              <span className="mb-1 block text-(--color-muted)">Email</span>
              <input name="email" type="email" required
                     className="w-full rounded border border-(--color-line) bg-(--color-canvas) px-2 py-1.5 text-body" />
            </label>
            <label className="text-small">
              <span className="mb-1 block text-(--color-muted)">Password (at least 12 characters)</span>
              <input name="password" type="password" minLength={12} required
                     className="w-full rounded border border-(--color-line) bg-(--color-canvas) px-2 py-1.5 text-body" />
            </label>
            <div className="flex gap-2">
              <button type="submit" disabled={addAgent.isPending}
                      className="rounded bg-(--color-brand) px-3 py-1.5 text-body font-medium text-white disabled:opacity-50">
                {addAgent.isPending ? 'Creating…' : 'Create'}
              </button>
              <button type="button" onClick={() => setAddingAgentTo(null)}
                      className="rounded border border-(--color-line) px-3 py-1.5 text-body">
                Cancel
              </button>
            </div>
          </form>
        </Dialog>
      )}
    </Page>
  );
}

function Action({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" onClick={onClick}
            className="rounded border border-(--color-line) px-2 py-1 text-small whitespace-nowrap hover:text-(--color-ink)">
      {children}
    </button>
  );
}

/** Centred, scrollable on a short screen, and closable with Escape. */
function Dialog({ title, onClose, children }: {
  title: string; onClose: () => void; children: React.ReactNode;
}) {
  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center overflow-y-auto bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}
    >
      <div className="w-full max-w-md rounded border border-(--color-line) bg-(--color-surface) p-5 shadow-lg">
        <h2 className="mb-3 text-body font-semibold">{title}</h2>
        {children}
      </div>
    </div>
  );
}
